import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import { ErrorBoundary } from '@/components/common/error-boundary';
import { ProtectedRoute } from '@/components/common/protected-route';
import { Spinner } from '@/components/ui/spinner';

const LoginPage = lazy(() => import('@/pages/login-page'));
const DashboardPage = lazy(() => import('@/pages/dashboard-page'));
const ChannelsPage = lazy(() => import('@/pages/channels-page'));
const ChannelDetailPage = lazy(() => import('@/pages/channel-detail-page'));
// Legacy "Stats" page === keyword search (no overview dashboard separate route).
const KeywordSearchPage = lazy(() => import('@/pages/keyword-search-page'));
const MessagesPage = lazy(() => import('@/pages/messages-page'));
const MessageDetailPage = lazy(() => import('@/pages/message-detail-page'));
const AccountsPage = lazy(() => import('@/pages/accounts-page'));
const BrandsPage = lazy(() => import('@/pages/brands-page'));
const CategoriesPage = lazy(() => import('@/pages/categories-page'));
const InsightsPage = lazy(() => import('@/pages/insights-page'));
const UsersPage = lazy(() => import('@/pages/users-page'));
const NotFoundPage = lazy(() => import('@/pages/not-found-page'));

function withSuspense(node: React.ReactNode) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner />
        </div>
      }
    >
      {node}
    </Suspense>
  );
}

const routes: RouteObject[] = [
  {
    path: '/login',
    element: withSuspense(<LoginPage />),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        errorElement: (
          <ErrorBoundary>
            <AppShell />
          </ErrorBoundary>
        ),
        children: [
          { index: true, element: withSuspense(<DashboardPage />) },
          { path: 'channels', element: withSuspense(<ChannelsPage />) },
          { path: 'channels/:id', element: withSuspense(<ChannelDetailPage />) },
          { path: 'messages', element: withSuspense(<MessagesPage />) },
          { path: 'messages/:id', element: withSuspense(<MessageDetailPage />) },
          { path: 'accounts', element: withSuspense(<AccountsPage />) },
          { path: 'brands', element: withSuspense(<BrandsPage />) },
          { path: 'categories', element: withSuspense(<CategoriesPage />) },
          { path: 'stats', element: withSuspense(<KeywordSearchPage />) },
          { path: 'insights', element: withSuspense(<InsightsPage />) },
          { path: 'users', element: withSuspense(<UsersPage />) },
        ],
      },
    ],
  },
  { path: '/404', element: withSuspense(<NotFoundPage />) },
  { path: '*', element: <Navigate to="/404" replace /> },
];

export const router = createBrowserRouter(routes);
